/**
 * Runtime Configuration Service — Learning Frontend
 *
 * Loads /assets/config.json at application startup (via APP_INITIALIZER).
 * On localhost the config is never found so getLocalhostConfig() provides the
 * same values as environment.stage.ts, keeping local dev working without changes.
 *
 * Mirrors the pattern used by LMS and PRO frontends so all services are
 * configured consistently from the same CDK-generated JSON.
 *
 * ---
 *
 * Service de configuration d'exécution — Frontend Learning
 *
 * Charge /assets/config.json au démarrage (via APP_INITIALIZER).
 * En local, le fallback getLocalhostConfig() fournit les mêmes valeurs
 * qu'environment.stage.ts.  Miroir du pattern LMS / PRO.
 */

import { Injectable } from '@angular/core';
import { environment } from '@tsin-env/environment';

export interface CognitoRuntimeConfig {
  userPoolId: string;
  clientId: string;
  region: string;
  domain: string;
  redirectSignIn: string;
  redirectSignOut: string;
  scope: string[];
  responseType: string;
}

export interface RuntimeConfig {
  production: boolean;
  environment: 'dev' | 'stage' | 'prod';
  /** Learning backend API base URL (e.g. https://learning.api.stage.tsin.ca/api) */
  apiUrl: string;
  /** Centralized Auth backend URL (e.g. https://auth.api.stage.tsin.ca) */
  authServiceUrl?: string;
  /** Centralized Auth-UI URL (e.g. https://auth.stage.tsin.ca) */
  authUiUrl?: string;
  cognito?: CognitoRuntimeConfig;
  generatedAt?: string;
}

@Injectable({ providedIn: 'root' })
export class ConfigService {
  private config: RuntimeConfig | null = null;

  /** Returns the loaded runtime config, falling back to localhost defaults. */
  getConfig(): RuntimeConfig {
    return this.config ?? this.getLocalhostConfig();
  }

  /** Called by APP_INITIALIZER once config.json is fetched. */
  setConfig(config: RuntimeConfig): void {
    this.config = config;
    console.log(`✅ [Learning] Runtime config loaded — env: ${config.environment.toUpperCase()}`);
    console.log('   API URL:', config.apiUrl);
    if (config.authUiUrl) console.log('   Auth UI:', config.authUiUrl);
    if (config.cognito)   console.log('   Cognito redirect:', config.cognito.redirectSignIn);
    else                  console.log('   Cognito: disabled (local auth)');
  }

  getEnvironment(): 'dev' | 'stage' | 'prod' {
    return this.config?.environment ?? 'dev';
  }

  getApiUrl(): string {
    return this.getConfig().apiUrl;
  }

  getCognitoConfig(): CognitoRuntimeConfig | undefined {
    return this.getConfig().cognito;
  }

  /**
   * Auth backend base URL — used for Cognito token exchange.
   * URL du backend Auth — utilisée pour l'échange de jeton Cognito.
   */
  getAuthServiceUrl(): string {
    return this.getConfig().authServiceUrl
      ?? (environment as any).authServiceUrl
      ?? 'https://auth.api.stage.tsin.ca';
  }

  /**
   * Auth-UI base URL — used to redirect unauthenticated users to SSO login.
   * URL de l'Auth-UI — utilisée pour rediriger les utilisateurs non authentifiés.
   */
  getAuthUiUrl(): string {
    return this.getConfig().authUiUrl
      ?? (environment as any).authUiUrl
      ?? 'https://auth.stage.tsin.ca';
  }

  // -------------------------------------------------------------------------
  // Private helpers
  // -------------------------------------------------------------------------

  private getLocalhostConfig(): RuntimeConfig {
    return {
      production: false,
      environment: 'dev',
      apiUrl: (environment as any).learningBaseUrl ?? 'http://localhost:8010/api',
      authServiceUrl: (environment as any).authServiceUrl,
      authUiUrl: (environment as any).authUiUrl,
      // No Cognito block on localhost — local auth flow is used.
    };
  }
}
